/**
 * "Exceptions & corrective actions" under the Reports grid. Express sends the
 * week's entries (oldest first) — this only words them.
 */

import moment from 'moment'
import {
  FOOD_SAFETY_EXCEPTION_KIND,
  FOOD_SAFETY_EXCEPTION_KIND_LABELS,
  FOOD_SAFETY_EXCEPTION_STATUS,
  FOOD_SAFETY_EXCEPTION_STATUS_LABELS,
  FOOD_SAFETY_SECTION_LABELS,
} from 'src/constants/foodSafety'
import {
  foodSafetyCalibrationTestLabel,
  formatFoodSafetyExceptionHeadline,
  formatFoodSafetyTemp,
} from 'src/utils/foodSafetyCorrectiveAction'

const time = at => {
  if (!at) return null
  const m = moment(at)
  return m.isValid() ? m.format('HH:mm') : null
}

const joinBits = bits => bits.filter(Boolean).join(' · ')

const clean = value => String(value || '').trim()

/** Joins the non-empty bits, dropping repeats ("Milk · Milk" → "Milk"). */
export const joinDistinctBits = (bits, { skip = [] } = {}) => {
  const seen = new Set(skip.map(b => clean(b).toLowerCase()).filter(Boolean))
  return bits
    .map(clean)
    .filter(bit => {
      const key = bit.toLowerCase()
      if (!bit || seen.has(key)) return false
      seen.add(key)
      return true
    })
    .join(' · ')
}

/** Express only adds `exceptions` for V2-aware builds; null = don't show the section. */
export function readReportExceptions(report) {
  return Array.isArray(report?.exceptions) ? report.exceptions : null
}

/** Open exceptions from before this week. Missing on old payloads. */
export function readEarlierOpenReportExceptions(report) {
  return Array.isArray(report?.earlierOpenExceptions)
    ? report.earlierOpenExceptions
    : []
}

/** "1 from earlier is still open." / "3 from earlier are still open." */
export function formatEarlierOpenExceptionsLine(exceptions) {
  const count = Array.isArray(exceptions)
    ? exceptions.length
    : Number(exceptions) || 0
  if (count <= 0) return null
  return count === 1
    ? '1 from earlier is still open.'
    : `${count} from earlier are still open.`
}

/** "3 raised · 2 actioned · 1 unresolved" */
export function formatReportExceptionsSummary(summary, exceptions = []) {
  const raised = Number(summary?.exceptionsRaised ?? exceptions.length) || 0
  if (!raised) return null
  const open =
    Number(
      summary?.exceptionsUnresolved ??
        exceptions.filter(e => e?.status === FOOD_SAFETY_EXCEPTION_STATUS.OPEN)
          .length,
    ) || 0
  const actioned = Number(summary?.exceptionsActioned ?? raised - open) || 0
  return joinBits([
    `${raised} raised`,
    `${actioned} actioned`,
    open ? `${open} unresolved` : null,
  ])
}

export function formatReportExceptionDay(localDate) {
  const m = moment(localDate, 'YYYY-MM-DD', true)
  return m.isValid() ? m.format('ddd D MMM') : localDate || ''
}

/** "Walk-in fridge" / "Milk · Brakes" — what the reading was for. */
export function formatReportExceptionName(exception) {
  const context = exception?.context || {}
  return (
    joinDistinctBits([
      exception?.definitionName,
      context.productName,
      context.supplierName,
    ]) || 'Reading'
  )
}

export function formatReportExceptionSection(exception) {
  return (
    exception?.sectionLabel ||
    FOOD_SAFETY_SECTION_LABELS[exception?.section] ||
    exception?.section ||
    ''
  )
}

/**
 * What went wrong, e.g. "⚠ 9°C — outside 0–5°C". Prefers Express's
 * `headline` (probe expected value, checklist issue text).
 */
export function formatReportExceptionHeadline(exception) {
  const headline = clean(exception?.headline)
  if (headline) return `⚠ ${headline}`
  const fallback = formatFoodSafetyExceptionHeadline(exception)
  const issue = clean(exception?.issue || exception?.context?.notes)
  if (
    exception?.kind === FOOD_SAFETY_EXCEPTION_KIND.CHECKLIST_ISSUE &&
    issue &&
    !fallback.toLowerCase().includes(issue.toLowerCase())
  ) {
    return `${fallback} — ${issue}`
  }
  return fallback
}

const DISHWASHER_FAILURE_LABEL = 'Dishwasher temperature check failed'

const isDishwasherFailure = exception => {
  const test = exception?.context?.calibrationTest
  return (
    exception?.section === 'dishwashers' ||
    exception?.context?.equipmentKind === 'dishwasher' ||
    test === 'wash' ||
    test === 'rinse'
  )
}

/** Kind label, or '' when the headline already says it. */
export function formatReportExceptionKind(exception) {
  const kind =
    exception?.kind === FOOD_SAFETY_EXCEPTION_KIND.CALIBRATION_FAILED &&
    isDishwasherFailure(exception)
      ? DISHWASHER_FAILURE_LABEL
      : exception?.kindLabel ||
        FOOD_SAFETY_EXCEPTION_KIND_LABELS[exception?.kind] ||
        ''
  if (!kind || exception?.showKind === false) return ''
  const headline = formatReportExceptionHeadline(exception).toLowerCase()
  if (headline.includes(kind.toLowerCase())) return ''
  const testLabel = foodSafetyCalibrationTestLabel(exception).toLowerCase()
  if (
    exception?.kind === FOOD_SAFETY_EXCEPTION_KIND.CALIBRATION_FAILED &&
    testLabel &&
    headline.startsWith(`⚠ ${testLabel}: probe read`)
  ) {
    return ''
  }
  return kind
}

/** "Raised 09:12 by Sam" */
export function formatReportExceptionRaised(exception) {
  const at = time(exception?.raisedAt)
  const by = String(exception?.raisedByName || '').trim()
  if (!at && !by) return null
  return `Raised${at ? ` ${at}` : ''}${by ? ` by ${by}` : ''}`
}

export function formatReportExceptionStatus(exception) {
  return (
    exception?.statusLabel ||
    FOOD_SAFETY_EXCEPTION_STATUS_LABELS[exception?.status] ||
    ''
  )
}

export const isReportExceptionOpen = exception =>
  exception?.status === FOOD_SAFETY_EXCEPTION_STATUS.OPEN

/** Unresolved first; otherwise Express's order (oldest first). */
export const sortReportExceptionsUnresolvedFirst = (exceptions = []) =>
  (exceptions || [])
    .map((exception, index) => ({ exception, index }))
    .sort(
      (a, b) =>
        Number(isReportExceptionOpen(b.exception)) -
          Number(isReportExceptionOpen(a.exception)) || a.index - b.index,
    )
    .map(({ exception }) => exception)

/** Pre-V2 reading note — only while nothing has been recorded against it. */
export const showReportLegacyNote = exception =>
  exception?.legacy === true &&
  !(exception?.correctiveActions || []).length &&
  !exception?.recheck

/** Older readings flagged after the fact can't take an action. */
export const canActionReportException = exception =>
  isReportExceptionOpen(exception) && exception?.legacy !== true

/** "Discarded food — binned the batch · Sam · 14:05" */
export function formatReportCorrectiveActionLine(action) {
  const label = String(action?.label || '').trim()
  const notes = String(action?.notes || '').trim()
  const what =
    String(action?.text || '').trim() ||
    (label && notes ? `${label} — ${notes}` : label || notes)
  return joinBits([
    what || 'Action recorded',
    action?.recordedByName,
    time(action?.recordedAt),
  ])
}

/** "Recheck 4°C · back in range · Sam · 14:30" */
export function formatReportRecheckLine(recheck) {
  if (!recheck || recheck.value == null) return null
  return joinBits([
    `Recheck ${formatFoodSafetyTemp(recheck.value)}`,
    clean(recheck.resultLabel) ||
      (recheck.outOfRange ? 'still outside range' : 'back in range'),
    recheck.recordedByName,
    time(recheck.recordedAt),
  ])
}
